/**
 * The one rule for what a vault document may be CALLED.
 *
 * A document's `filename` is chosen by whoever uploaded it, stored, shown in the
 * document list, and handed to `a.download`, which is what names the file on the
 * downloading member's disk. A workspace can hold several people (an advisor, a
 * family-office arrangement), so "the uploader" and "the person double-clicking it"
 * are not always the same person, and the uploader gets to choose what the other one
 * believes they are opening (steward-app#525).
 *
 * Appcore is not doing nothing on write: its own `sanitizeFilename` takes the basename,
 * replaces everything outside `[A-Za-z0-9._ ()+-]` with `_`, and caps at 200. That
 * already lands an RTL override as `_` for anything stored since it existed. It does
 * NOT touch the other half of #525, because every character of `.pdf.exe` is inside
 * that class, and it does nothing for rows written before it. Two consequences worth
 * keeping in mind while reading this file: the launcher-extension rule below is the
 * part that is load-bearing for new uploads, and appcore's 200-cap is quietly what
 * keeps a very long name from ever reaching the repair path here.
 *
 * Two spoofs make that concrete:
 *
 *   - **A right-to-left override.** `statement<U+202E>fdp.exe` renders as
 *     `statementexe.pdf` in the document list, in the OS file dialog, and in the
 *     downloads shelf. Nothing on the path strips format characters today, so the
 *     display lies from end to end.
 *   - **A double extension.** `2025-statement.pdf.exe` is displayed in full by this
 *     app, but Windows Explorer hides known extensions by default, so on disk it reads
 *     as `2025-statement.pdf` and runs when opened.
 *
 * Path traversal is deliberately NOT what this module is about. The S3 key is derived
 * server-side from the entity id and is never client-supplied, and `a.download` already
 * flattens separators. This is only about the name, and about the gap between the name
 * a member reads and the name their operating system acts on.
 *
 * ## Two entry points, one rule
 *
 * `safeDocumentFilename` repairs a name; `documentFilenameProblem` reports whether one
 * needs repair. They are two views of the same list of rules, and the invariant that
 * ties them together is enforced by a test: the output of `safeDocumentFilename` always
 * satisfies `documentFilenameProblem(...) === null`. That invariant is what lets the
 * SPA sanitize before it uploads while the façade API validates on the way in, without
 * the two drifting into a state where the SPA sends something the API refuses.
 *
 * Both boundaries are covered on purpose, and so is a third:
 *
 *   - **On the way in** (`documents.ts` request schemas) so nothing new is stored bad.
 *   - **On the way out** (`toDocument` / `toVersion`) because inbound validation does
 *     nothing for the rows already in the table. Every name already stored predates
 *     this module and none of them was checked.
 *   - **At the sink** (`src/lib/download.ts`) because that assignment to `a.download`
 *     is the line that actually names a file on someone's disk, and it should not
 *     depend on a response having come from a version of the API that sanitizes.
 */

import { UNSAFE_CHARS } from './unsafeCharacters.js';

/**
 * The longest name we keep.
 *
 * NTFS and ext4 both stop at 255, so 200 is not the filesystem limit; it is the
 * filesystem limit with room left over for the ` (1)` a browser appends when the name
 * collides with something already in the downloads folder, and for a name that is
 * measured here in UTF-16 units but written to disk as UTF-8, where a non-Latin name
 * can be two or three bytes per character.
 */
export const MAX_DOCUMENT_FILENAME_LENGTH = 200;

/**
 * Characters that never survive.
 *
 * This is the shared list, not a copy of it. `UNSAFE_CHARS` lives in
 * `unsafeCharacters.ts` and is the same definition the display-text rule consumes
 * (steward-app#600); it used to be spelled out here, and a second spelling of it
 * elsewhere is exactly how the two would have drifted apart. That module carries the
 * full account of what is in the class and what including it costs. The short version:
 * `\p{Cc}` is the control characters, `\p{Cf}` is the format family and is what kills
 * the RTL-override spoof, `\p{Cs}` is unpaired surrogates.
 *
 * A filename takes the STRICT form of the class, with no exception for tab or newline.
 * A filename is a single line by definition, and a name arriving with a newline in it
 * did not come from someone naming a file. Display text takes the other form, because a
 * member's note is entitled to its paragraphs.
 */
const FORBIDDEN_CHARS = UNSAFE_CHARS;

/**
 * Path separators, plus the characters Windows refuses in a name.
 *
 * The separators are not here for traversal (see the header) but because a name
 * containing one is a name that no longer means what it reads as once some layer
 * flattens it. `:` earns its place twice over: it is an NTFS alternate-data-stream
 * marker, so `statement.pdf:payload.exe` is a name with a second file hiding in it.
 */
const REPLACED_CHARS = /[/\\<>:"|?*]/g;

/**
 * MS-DOS device names, which Windows still resolves ahead of a real file no matter
 * what extension follows (`nul.pdf` is the null device, not a PDF). Matched on the
 * stem, case-insensitively, since `Con.txt` and `CON.TXT` are the same device.
 */
const RESERVED_DEVICE_NAME = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])(\.|$)/i;

/** Trailing dots and spaces, which Windows silently strips when it creates the file. */
const TRAILING_DOTS_AND_SPACES = /[. ]+$/;

/**
 * Extensions an operating system will RUN or LAUNCH from a double-click.
 *
 * This is a denylist of launchers, not an allowlist of document types, and that is a
 * deliberate trade. An allowlist would be stronger and would also be wrong for a family
 * vault, where the long tail is real: a surveyor's `.dwg`, an accountant's `.qbb`, a
 * detached `.p7s` signature, a `.dat` some institution's portal produced. Refusing
 * those to close a hole nobody has is the wrong direction for this product.
 *
 * Macro-bearing Office DOCUMENTS (`.docm`, `.xlsm`, `.pptm`) are deliberately absent.
 * They are documents, a vault is exactly where they belong, and Office prompts before
 * running the macro. Office ADD-INS (`.xll`, `.xlam`, `.xla`, `.ppam`) are a different
 * thing wearing a similar suffix and ARE on the list: an `.xll` is a native DLL that
 * Excel loads with no prompt at all.
 *
 * `.html`, `.htm`, `.svg` and friends are also absent, after weighing them. They do not
 * execute against the operating system, only inside the browser sandbox, and a saved
 * statement page or a logo is an ordinary thing to keep in a vault. Appcore separately
 * refuses `text/html` and the whole `image/svg` family at upload.
 */
const DANGEROUS_EXTENSIONS = new Set([
  // Windows executables, libraries and installers
  'exe', 'com', 'scr', 'pif', 'cpl', 'msi', 'msp', 'msc', 'mst', 'msu', 'dll', 'ocx',
  'sys', 'drv', 'msix', 'msixbundle', 'appx', 'appxbundle', 'appinstaller',
  // Windows shells, shortcuts and "open me" file types that run a command
  'bat', 'cmd', 'lnk', 'url', 'scf', 'shs', 'shb', 'inf', 'ins', 'isp', 'reg', 'hta',
  'chm', 'hlp', 'job', 'application', 'appref-ms', 'gadget', 'diagcab', 'ppkg', 'udl',
  'printerexport', 'website', 'settingcontent-ms', 'library-ms', 'search-ms',
  'searchconnector-ms', 'theme', 'themepack', 'deskthemepack', 'wsb',
  // Script hosts
  'js', 'jse', 'vb', 'vbs', 'vbe', 'wsf', 'wsh', 'ws', 'wsc', 'sct', 'ps1', 'ps1xml',
  'ps2', 'ps2xml', 'psc1', 'psc2', 'psm1', 'msh', 'msh1', 'msh2', 'mshxml', 'ahk',
  'jar', 'jnlp', 'xbap', 'py', 'pyw', 'pyz', 'pyzw', 'pl', 'rb',
  // Office ADD-INS (not macro-bearing documents — see the note above) and the
  // data-file formats whose whole purpose is to fetch and run something else
  'xll', 'xlam', 'xla', 'ppam', 'iqy',
  // Microsoft Access, which runs VBA on open
  'mdb', 'mda', 'mde', 'accdb', 'accde', 'ade', 'adp',
  // Browser and IDE extension packages, which install code on open
  'crx', 'xpi', 'vsix',
  // Disk images: they mount on a double-click, which is the standard way a payload is
  // delivered past mark-of-the-web today
  'iso', 'img', 'vhd', 'vhdx', 'vhdset', 'udf',
  // macOS
  'app', 'command', 'tool', 'workflow', 'action', 'scpt', 'scptd', 'terminal', 'pkg',
  'mpkg', 'dmg', 'mobileconfig', 'inetloc', 'webloc', 'fileloc',
  // Linux and cross-platform
  'sh', 'bash', 'zsh', 'ksh', 'csh', 'run', 'appimage', 'desktop', 'deb', 'rpm', 'apk',
]);

/**
 * A name split into its stem and its final extension, where the extension INCLUDES the
 * dot and is `''` when there is no usable one.
 *
 * A leading dot counts. `.exe` is a stemless name whose extension Windows still honours,
 * so it runs; treating it as a dotfile with no extension would let exactly that through.
 * A trailing dot does not: there is nothing after it to be an extension.
 *
 * One helper rather than three copies of `lastIndexOf('.')`, because the demotion, the
 * validation and the truncation all have to agree on where the extension starts. They
 * did not agree in the first cut of this file, and the disagreement was a live hole:
 * see the fixed-point loop in `safeDocumentFilename`.
 */
function splitExtension(name: string): { stem: string; ext: string } {
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot === name.length - 1) return { stem: name, ext: '' };
  return { stem: name.slice(0, dot), ext: name.slice(dot) };
}

/** The final extension without its dot, lowercased, or `''`. */
function finalExtension(name: string): string {
  return splitExtension(name).ext.slice(1).toLowerCase();
}

/**
 * Why this name is not acceptable, phrased for a person to read, or `null` when it is
 * fine. Callers that validate use this; callers that repair use `safeDocumentFilename`.
 *
 * The messages name the rule rather than echoing the value, because the value is by
 * definition the thing nothing has vouched for.
 */
export function documentFilenameProblem(name: string): string | null {
  if (name === '') return 'A file name is required.';
  if (name.length > MAX_DOCUMENT_FILENAME_LENGTH) {
    return `File names must be ${MAX_DOCUMENT_FILENAME_LENGTH} characters or fewer.`;
  }
  // `replace` rather than `test`: both patterns carry the `g` flag for the repair path,
  // and a global regex's `test` walks `lastIndex` from call to call. `String.replace`
  // has no such state.
  if (name.replace(FORBIDDEN_CHARS, '') !== name) {
    return 'File names cannot contain control or invisible formatting characters.';
  }
  if (name.replace(REPLACED_CHARS, '_') !== name) {
    return 'File names cannot contain / \\ : * ? " < > or |.';
  }
  if (name !== name.trim() || TRAILING_DOTS_AND_SPACES.test(name)) {
    return 'File names cannot begin or end with a space, or end with a dot.';
  }
  if (RESERVED_DEVICE_NAME.test(name)) {
    return 'That file name is reserved by Windows. Please choose another.';
  }
  if (DANGEROUS_EXTENSIONS.has(finalExtension(name))) {
    return 'Files that a computer would run when opened cannot be stored in the vault.';
  }
  return null;
}

/**
 * The same name, made safe to store, to show, and to write to a disk. Always returns
 * something usable: a name that repairs down to nothing becomes `document`.
 *
 * Repair rather than refusal, everywhere, for three reasons. The rows already stored
 * cannot be refused, only repaired, so the repair path has to exist regardless, and one
 * behaviour that both boundaries share is one behaviour that cannot drift. It means the
 * name shown in the vault and the name written to disk are the same string, which is
 * the property that actually defeats the spoof: the display and the disk cannot
 * disagree if there is only one name. And a member who names a file with an emoji or a
 * Persian ZWNJ gets it cleaned instead of getting an upload refused.
 *
 * Order is load-bearing, and getting it right needs a fixed point rather than a
 * sequence. The forbidden characters go first, so everything after sees the name the OS
 * will see: `statement<U+202E>fdp.exe` is only visibly a `.exe` once the override is
 * gone. After that, each repair can create work for another: truncating exposes a
 * trailing dot, stripping that trailing dot exposes a different final extension, and
 * demoting an extension changes what the name ends in. Running them once in some
 * chosen order left a real hole, found in review of the first cut of this file:
 * `'x'.repeat(196) + '.exe' + '.' + 'y'.repeat(300)` came out as a live `….exe`,
 * because the truncation that revealed it ran after the only extension check. So the
 * repairs now run to a fixed point and the module's invariant is a property of the
 * loop rather than of an argument about ordering.
 */
export function safeDocumentFilename(name: string | null | undefined): string {
  // Bound the input before any regex sees it. `TRAILING_DOTS_AND_SPACES` is unanchored
  // at its start, so on a pathological `'.'.repeat(n) + 'a'` it backtracks from every
  // index — measurably quadratic past about 20k characters. Nothing legitimate is near
  // this bound, and everything is cut to MAX below anyway.
  let s = String(name ?? '').slice(0, MAX_DOCUMENT_FILENAME_LENGTH * 4);

  s = s.replace(FORBIDDEN_CHARS, '');
  // A separator or an illegal character becomes `_` rather than vanishing, so a name
  // does not quietly close up around what was removed (`x/etc` reading as `xetc`).
  s = s.replace(REPLACED_CHARS, '_');
  s = tidyEdges(s);

  // Before the loop, because only the START of the name decides this and the loop only
  // ever removes from the end. Doing it after would also push a name at the cap one
  // character over it.
  if (RESERVED_DEVICE_NAME.test(s)) s = `_${s}`;

  // Demote, cut, tidy, repeat. Each pass can only shorten the name or leave it alone,
  // so this settles; the bound is a guard, not a schedule.
  for (let pass = 0; pass < 8; pass++) {
    const next = tidyEdges(truncate(demoteDangerousExtension(s), MAX_DOCUMENT_FILENAME_LENGTH));
    if (next === s) break;
    s = next;
  }

  return s === '' ? 'document' : s;
}

/** Leading and trailing whitespace, and the trailing dots Windows would strip anyway. */
function tidyEdges(s: string): string {
  return s.trim().replace(TRAILING_DOTS_AND_SPACES, '').trim();
}

/**
 * Neutralize a launcher extension by demoting the dot that introduces it, which leaves
 * the name legible and self-explanatory (`2025-statement.pdf.exe` becomes
 * `2025-statement.pdf_exe`) instead of deleting evidence or asserting a type we do not
 * actually know.
 *
 * Deriving the extension from the stored `mimeType` was the other candidate and was
 * rejected: `mimeType` arrives from the same uploader through the same request, so it is
 * not the more trustworthy of the two, and it is routinely `application/octet-stream`
 * from a browser, which would rename honest files to `.bin`.
 *
 * Only the FINAL extension matters; `notes.exe.txt` is inert and is left be. The result
 * is a fixed point of this function: no entry on the list contains `_`, so a demoted
 * name cannot demote again.
 */
function demoteDangerousExtension(s: string): string {
  const { stem, ext } = splitExtension(s);
  return DANGEROUS_EXTENSIONS.has(ext.slice(1).toLowerCase()) ? `${stem}_${ext.slice(1)}` : s;
}

/**
 * Cut to `max` UTF-16 units, keeping the extension, which is the part a person reads to
 * decide what the file is. A stem is trimmed down to make room; a name that is one
 * enormous suffix falls through to the plain cut, and the caller's loop re-checks what
 * that cut left behind.
 *
 * The cut never lands between a surrogate pair: half of an astral character is not a
 * character, and it does not survive a round trip through UTF-8.
 */
function truncate(s: string, max: number): string {
  if (s.length <= max) return s;

  const { stem, ext } = splitExtension(s);
  // `+ 8` keeps a recognizable stem: an extension that would leave less than that is
  // not worth preserving, so the name is simply cut.
  if (ext.length > 0 && ext.length + 8 <= max) return cut(stem, max - ext.length) + ext;
  return cut(s, max);
}

function cut(s: string, max: number): string {
  const out = s.slice(0, max);
  const last = out.charCodeAt(out.length - 1);
  // A high surrogate at the end lost its partner to the slice.
  return last >= 0xd800 && last <= 0xdbff ? out.slice(0, -1) : out;
}
